type FrameCallback = () => void

const callbacks = new Set<FrameCallback>()

let frame = 0

const flush = (): void => {
    frame = 0
    callbacks.forEach(callback => callback())
}

const schedule = (): void => {
    if (frame) return
    frame = window.requestAnimationFrame(flush)
}

/**
 * Runs `callback` once per animation frame while the page scrolls or resizes.
 * Call it only from an effect (it touches window). Returns an unsubscribe function.
 */
export const onScrollFrame = (callback: FrameCallback): (() => void) => {
    if (callbacks.size === 0) {
        window.addEventListener('scroll', schedule, { passive: true })
        window.addEventListener('resize', schedule)
    }

    callbacks.add(callback)
    schedule()

    return () => {
        callbacks.delete(callback)

        if (callbacks.size > 0) return

        window.removeEventListener('scroll', schedule)
        window.removeEventListener('resize', schedule)

        if (frame) {
            window.cancelAnimationFrame(frame)
            frame = 0
        }
    }
}

export const prefersReducedMotion = (): boolean => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export const clamp = (value: number, min = 0, max = 1): number => {
    return Math.min(max, Math.max(min, value))
}
